import { z } from 'zod';
import { patchSchema } from './patch.js';

export const MAIL_THEMES = ['dark', 'light'] as const;
export type MailTheme = (typeof MAIL_THEMES)[number];

/** Mail kinds that count as invoice mail for `mailStyle.invoices`: the invoice itself and every reminder after it. */
export const INVOICE_MAIL_KINDS = ['invoice', 'reminder', 'demand_1', 'demand_2'] as const;

/**
 * A company's house style for outgoing mail: the layout (header with the
 * logo, a card for the text, a footer) put around a mail's body when it is
 * sent. Stored on the company; a missing or broken value means the defaults.
 */
const accentColor = z.string().regex(/^#[0-9a-f]{6}$/i, 'Expected a colour like #3B82F6');

export const mailStyleSchema = z.object({
  /** Wrap outreach (campaign and test) mails. */
  outreach: z.boolean().default(true),
  /** Wrap invoice mails and their reminders and demands. */
  invoices: z.boolean().default(false),
  theme: z.enum(MAIL_THEMES).default('dark'),
  /** The rule under the header, links and table accents, as #RRGGBB. */
  accentColor: accentColor.default('#3B82F6'),
  /** A white badge behind the logo, for a logo that needs a light background on the dark theme. */
  logoBadge: z.boolean().default(true),
  /** Footer text; empty means the company name and address. */
  footerText: z.string().max(500).nullable().default(null),
});
export type MailStyle = z.output<typeof mailStyleSchema>;

/** A change to the style: only the fields given (see patchSchema). */
export const updateMailStyleSchema = patchSchema(mailStyleSchema);
export type UpdateMailStyleDto = z.input<typeof updateMailStyleSchema>;

export const mailStylePreviewSchema = z.object({
  style: updateMailStyleSchema.default({}),
  lang: z.enum(['en', 'nl']).default('nl'),
});
